// Decyzon — universal referral attribution.
//
// Two responsibilities:
//   1. Capture an inbound `?ref=<code>` from any landing URL and persist it
//      so we can attribute a future signup / subscription to the referrer.
//   2. Provide the *outbound* ref code that should be appended to every
//      shared URL so the share loop is measurable end-to-end.
//
// The outbound code comes from (in priority order):
//   - the signed-in user's profile.share_handle (set on first share)
//   - a stable per-device anonymous id (so anonymous shares are still tracked)
//
// We deliberately keep this client-only for now. The server-side attribution
// will land with the affiliate v1 schema (referrals table). This module only
// guarantees that every shared URL carries a `?ref=` we can later reconcile.

const INBOUND_KEY = "decyzon.ref.inbound";
const OUTBOUND_KEY = "decyzon.ref.outbound";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

interface StoredRef {
  code: string;
  capturedAt: number;
}

function readStored(key: string): StoredRef | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    if (!parsed?.code) return null;
    if (Date.now() - parsed.capturedAt > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(key: string, code: string) {
  try {
    localStorage.setItem(key, JSON.stringify({ code, capturedAt: Date.now() }));
  } catch {
    /* swallow — private mode etc */
  }
}

/**
 * Capture `?ref=<code>` from the current URL. Idempotent. Strips the param
 * from the visible URL after capture so it doesn't leak into share cards.
 */
export function captureInboundRef(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("ref");
    if (!code) return;
    const safe = code.trim().slice(0, 64).replace(/[^a-zA-Z0-9_-]/g, "");
    if (!safe) return;

    // Don't overwrite an existing capture — first-touch attribution.
    if (!readStored(INBOUND_KEY)) {
      writeStored(INBOUND_KEY, safe);
    }
    // Strip the param from the visible URL.
    url.searchParams.delete("ref");
    const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : "") + url.hash;
    window.history.replaceState({}, "", cleaned);
  } catch {
    /* swallow */
  }
}

export function getInboundRef(): string | null {
  return readStored(INBOUND_KEY)?.code ?? null;
}

/**
 * Stable anonymous device id used as the outbound ref for users without a
 * proper share handle yet. Short, URL-safe, persistent.
 */
function getOrCreateDeviceRef(): string {
  const existing = readStored(OUTBOUND_KEY);
  if (existing) return existing.code;
  // 8-char base36 id — enough entropy for attribution, short for tweets.
  const code =
    "d" +
    Math.floor(Math.random() * 36 ** 8)
      .toString(36)
      .padStart(8, "0")
      .slice(0, 8);
  writeStored(OUTBOUND_KEY, code);
  return code;
}

/**
 * Set the outbound ref to the user's chosen handle. Called once we know
 * the signed-in user's share_handle (e.g. on profile load).
 */
export function setOutboundRef(handle: string | null | undefined): void {
  if (!handle) return;
  const safe = handle.trim().slice(0, 32).replace(/[^a-zA-Z0-9_-]/g, "");
  if (!safe) return;
  writeStored(OUTBOUND_KEY, safe);
}

export function getOutboundRef(): string {
  const stored = readStored(OUTBOUND_KEY);
  if (stored) return stored.code;
  return getOrCreateDeviceRef();
}

/**
 * Append the outbound ref to a URL. Preserves existing query params.
 * Safe to call repeatedly — won't add duplicates.
 */
export function withRef(url: string, refOverride?: string | null): string {
  try {
    const u = new URL(url);
    const code = refOverride ?? getOutboundRef();
    if (!code) return url;
    if (u.searchParams.get("ref")) return url;
    u.searchParams.set("ref", code);
    return u.toString();
  } catch {
    return url;
  }
}
